import { motion, useMotionValue } from 'framer-motion';
import { debounce } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { Description, Image, SubTitle } from '../../components/layouts';
import Sequence from '../../components/sequence';
import Visibility from '../../components/Visibility';
import { AppContext } from '../../Context';
import { buildImageURL, getSeqFrames } from '../../helpers';
import './style.scss';

const IMAGES = {
  l1: buildImageURL('/a-w-l1.png'),
  l2: buildImageURL('/a-w-l2.png'),
  c: buildImageURL('/a-w-c.png'),
  r2: buildImageURL('/a-w-r2.png'),
  r1: buildImageURL('/a-w-r1.png')
};

const variants_w = {
  visible: { y: -10 },
  hidden: { y: 0 }
};

const variants_left_1 = {
  visible: { x: '-210%', y: '-50%' },
  hidden: { x: '-50%', y: '-50%' }
};
const variants_left_2 = {
  visible: { x: '-140%', y: '-50%' },
  hidden: { x: '-50%', y: '-50%' }
};
const variants_right_2 = {
  visible: { x: '40%', y: '-50%' },
  hidden: { x: '-50%', y: '-50%' }
};
const variants_right_1 = {
  visible: { x: '100%', y: '-50%' },
  hidden: { x: '-50%', y: '-50%' }
};
const variants_shadow = {
  visible: { y: '0%', opacity: 1, scaleX: 1, scaleY: 0.4 },
  hidden: { y: '0%', opacity: 0, scaleX: 0.1, scaleY: 0.4 }
};
const transition_w = {
  y: {
    delay: 0,
    duration: 0.2
  }
};

const transition_l_r = {
  x: {
    delay: 0.2,
    duration: 0.8,
    ease: 'easeOut'
  }
};

const transition_shadow = {
  opacity: {
    delay: 0.6,
    duration: 0.8
  },
  y: {
    delay: 0.2,
    duration: 0.8
  },
  scaleX: {
    delay: 0.2,
    duration: 0.8
  }
};

const FRAME_LENGTH = 76;

const roll = setIndex =>
  debounce(function(indexDrawn) {
    setIndex(index => {
      if (index > indexDrawn) {
        return index;
      }
      if (indexDrawn === FRAME_LENGTH - 1) {
        // return 0;
        return indexDrawn;
      }
      return indexDrawn + 1;
    });
  }, 41);

export function ActivityWallpaper({ mobile = false }) {
  const [frozen, freeze] = useState(true);
  const onScreen = useMotionValue(false);
  const [framesLoaded, setFramesLoaded] = useState(false);
  const [index, setIndex] = useState(-1);
  const [animate, setAnimate] = useState('hidden');

  const { webpSupport, webpDetectionFinished } = React.useContext(AppContext);

  const frames = useMemo(() => {
    return getSeqFrames({
      prefix: 'wallpaper',
      mobile: false,
      ext: 'png',
      webpSupport: false,
      len: FRAME_LENGTH
    });
  }, [webpSupport, mobile]);

  React.useEffect(() => {
    if (animate && framesLoaded && index === -1) {
      setIndex(0);
    }
  }, [framesLoaded, animate, index, setIndex]);

  const onDrawn = useCallback(roll(setIndex, onScreen), [setIndex]);

  const handleUpdate = useCallback(
    (e, { calculations }) => {
      const { pixelsPassed, percentagePassed, height, offScreen } = calculations;
      freeze(offScreen || pixelsPassed <= -1000 || pixelsPassed >= height + 1000);
      onScreen.set(calculations.onScreen);
      if (animate !== 'hidden') {
        return;
      }
      if (percentagePassed > -0.1 && percentagePassed < 0.75) {
        setAnimate('visible');
      }
    },
    [onScreen, freeze, animate, setAnimate]
  );

  const onFramesLoaded = useCallback(() => {
    setFramesLoaded(true);
  }, [setFramesLoaded]);

  const placeholderVisible = !framesLoaded;

  const seqReady = !frozen && webpDetectionFinished;

  return (
    <Visibility offset={[-1000, 0]} fireOnMount onUpdate={handleUpdate} className="page-section section-activity_wallpaper inverted">
      <div className="section-content center">
        <SubTitle>行为壁纸</SubTitle>
        <Description>我们将花朵绽放的过程浓缩为短短几秒，这种健康的鼓励方式形成了物理世界和数字世界一个十分美妙的连接。</Description>
      </div>
      <motion.div className="images-wrapper" initial="hidden" animate={animate} variants={variants_w} transition={transition_w}>
        <motion.div className="i-l-1" initial="hidden" animate={animate} variants={variants_left_1} transition={transition_l_r}>
          <Image src={IMAGES.l1} />
        </motion.div>
        <motion.div className="i-l-2" initial="hidden" animate={animate} variants={variants_left_2} transition={transition_l_r}>
          <Image src={IMAGES.l2} />
        </motion.div>
        <div className="i-c">
          <div data-visible={placeholderVisible}>
            <Image src={frames[0]} loadingType="lazy" />
          </div>
          <div data-visible={!placeholderVisible}>
            <Sequence current={index} frames={frames} onload={onFramesLoaded} onDrawn={onDrawn} active={seqReady} />
          </div>
        </div>
        <motion.div className="i-r-2" initial="hidden" animate={animate} variants={variants_right_2} transition={transition_l_r}>
          <Image src={IMAGES.r2} />
        </motion.div>
        <motion.div className="i-r-1" initial="hidden" animate={animate} variants={variants_right_1} transition={transition_l_r}>
          <Image src={IMAGES.r1} />
        </motion.div>
        <div className="shadow-wrapper">
          <motion.div className="shadow" initial="hidden" animate={animate} variants={variants_shadow} transition={transition_shadow} />
        </div>
      </motion.div>
    </Visibility>
  );
}
